import * as React from 'react'

import { READ_ALOUD_BUTTON_VARIANT, ReadAloudButtonVariant } from './constants'
import { useReadAloud } from './readAloudContext'
import { ReadAloudSpeakerToggle } from './readAloudSpeakerToggle'
import { SpeakerIcon } from './speechIcons'
import { useReadAloudSource } from './readAloudSource'

import useTextToSpeech from '../../hooks/readAloud/useTextToSpeech'
import { ClipSource, staticStringSourceFor } from '../../libs/speech/clipSource'
import { prepareSpeech, warmSpeech } from '../../libs/speech/speechManager'

interface ReadAloudButtonProps {
  activeText?: string;
  className?: string;
  controlsId?: string;
  focusOnDark?: boolean;
  idleText?: string;
  label?: string;
  onBoundary?: (charIndex: number) => void;
  onSpeechEnd?: () => void;
  source?: ClipSource | null;
  text: string;
  variant?: ReadAloudButtonVariant;
}

const ReadAloudButton = ({ activeText = 'Stop listening', className, controlsId, focusOnDark, idleText = 'Listen', label, onBoundary, onSpeechEnd, source, text, variant, }: ReadAloudButtonProps) => {
  const inheritedSource = useReadAloudSource()
  const clipSource = source ?? staticStringSourceFor(text) ?? inheritedSource
  const { readAloudEnabled, } = useReadAloud()
  const { cancel, isSpeaking, isSupported, speak, } = useTextToSpeech({ onBoundary, onEnd: onSpeechEnd, })

  React.useEffect(() => {
    if (!readAloudEnabled || !text) { return }

    prepareSpeech([{ source: clipSource, text, }]).then(() => warmSpeech(text))
  }, [readAloudEnabled, text])

  const handleSpeak = () => speak(text)

  const handleClick = () => {
    if (isSpeaking) {
      cancel()
    } else {
      handleSpeak()
    }
  }

  if (!readAloudEnabled || !isSupported || !text || !text.trim()) { return null }

  const focusClass = focusOnDark ? 'focus-on-dark' : 'focus-on-light'

  if (variant === READ_ALOUD_BUTTON_VARIANT.LISTEN) {
    return (
      <button
        aria-controls={controlsId}
        aria-pressed={isSpeaking}
        className={`listen-button quill-button medium outlined grey ${focusClass} ${isSpeaking ? 'is-playing' : ''} ${className || ''}`}
        onClick={handleClick}
        type="button"
      >
        <SpeakerIcon />
        <span>{isSpeaking ? activeText : idleText}</span>
      </button>
    )
  }

  return (
    <ReadAloudSpeakerToggle
      ariaControls={controlsId}
      className={className}
      focusOnDark={focusOnDark}
      label={label || 'Read aloud'}
      onPlay={handleSpeak}
      onStop={cancel}
      playing={isSpeaking}
    />
  )
}

export { ReadAloudButton }
